import { PropsWithChildren } from "react";
import { Box } from "@mui/material";
import { TableTraderEntity } from "src/components-mui/base/table-scrollable/components/entity/table-trader-entity";
import { TagIcon, ITagIconTag, MAX_ROUND_TAGS_COMBO_WIDTH } from "src/components-mui/base/table-scrollable/components/entity/tag-icon";
import { useTableScrollableSnapshot } from "src/components-mui/base/table-scrollable/store/table-scrollable-store";
import { ITraderAvatarProps, ITraderAvatarUser } from "src/components-mui/common/trader-avatar/trader-avatar";

interface ITableEntityWithTagsLayoutProps {
    tags?: string[];
    tableTags?: ITagIconTag[];
    /** When true, tags are rendered right-aligned next to the entity so their right edges align across rows. */
    showTagsColumn: boolean;
}

export const TableEntityWithTagsLayout = ({ children, tags, tableTags, showTagsColumn }: PropsWithChildren<ITableEntityWithTagsLayoutProps>) => {
    const { shrinkFirstColumn } = useTableScrollableSnapshot();
    const renderTags = showTagsColumn && !shrinkFirstColumn;

    return (
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, gap: 1 }}>
            {/* On lg+ the entity flexes into the space left of the fixed tag slot, so names always truncate before
                the tag column — no text ever renders inside the tag zone of neighbouring rows.
                On sm/md the entity sizes to content so tags float with the text. */}
            <Box sx={{ minWidth: 0, flex: renderTags ? { xs: "0 1 auto", lg: "1 1 0" } : 1 }}>{children}</Box>
            {renderTags && (
                <Box
                    sx={{
                        display: { xs: "none", sm: "flex" },
                        flexShrink: 0,
                        alignItems: "center",
                        // Fixed slot on lg+ (like the invested-icon placeholder): reserved on every row so
                        // icons start at the same x across all rows, tagged or not.
                        width: { lg: `${MAX_ROUND_TAGS_COMBO_WIDTH}px` },
                    }}
                >
                    <TagIcon tableTags={tableTags} tags={tags} showCircleFromSm variant="round" />
                </Box>
            )}
        </Box>
    );
};

interface ITableTraderEntityWithTagsProps {
    wikifolioShortDescription: string;
    wikifolioSymbol: string;
    trader: ITraderAvatarUser;
    tags?: string[];
    tableTags?: ITagIconTag[];
    showTagsColumn: boolean;
    openLinksInNewTab?: boolean;
    avatarLoading?: NonNullable<ITraderAvatarProps["loading"]>;
}

export const TableTraderEntityWithTags = ({
    wikifolioShortDescription,
    wikifolioSymbol,
    trader,
    tags,
    tableTags,
    showTagsColumn,
    openLinksInNewTab,
    avatarLoading,
}: ITableTraderEntityWithTagsProps) => (
    <TableEntityWithTagsLayout tags={tags} tableTags={tableTags} showTagsColumn={showTagsColumn}>
        <TableTraderEntity
            wikifolioShortDescription={wikifolioShortDescription}
            wikifolioSymbol={wikifolioSymbol}
            trader={trader}
            openLinksInNewTab={openLinksInNewTab}
            avatarLoading={avatarLoading}
        />
    </TableEntityWithTagsLayout>
);
